import type Luminary from '@lumen5/luminary';
import { ActionCreators } from 'redux-undo';

import { isSceneGroupLoading } from 'js/src/data/app';
import {
    clearFocusedAsset,
    hideFullscreenSelector,
    hideFullscreenSwapSceneSelector,
    hideOverlaySelector,
} from 'js/src/data/scenes';
import { getTemplateBundle } from 'js/src/data/templates/templates-selectors';
import { startLexicalControllingItsOwnState } from 'js/src/data/transcript/transcript-actions';
import { hydrateLexicalEditorsFromRedux } from 'js/src/data/transcript/transcript-operations';
import { hydrateTranslatedTranscriptsFromRedux } from 'js/src/data/translated-editors/translated-editors-operations';
import { checkpointVideo, getVideoData } from 'js/src/data/video-data';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import uuid from 'js/src/libs/uuid';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import { saveRawLuminary } from 'js/src/luminary/utils';
import ActivityProfiler from 'js/src/profiling/ActivityProfiler';
import { METRIC_UNDO_REDO } from 'js/src/profiling/metrics';

const performUndoRedoOperation = createLuminaryOperation(
    ({ luminary, dispatch, getState }, undoOrRedoAction: typeof ActionCreators.undo | null) => {
        // If a scene group is loading, don't let the user perform undo/redo until the changes are applied
        const isAnySceneGroupLoading = luminary.sceneGroups.some((sceneGroup) =>
            isSceneGroupLoading(getState(), sceneGroup.id),
        );
        if (isAnySceneGroupLoading) {
            return;
        }
        // track how long it takes for all the scene design previews to be updated
        // when clicking 'undo'
        const activityId = uuid();
        ActivityProfiler.start(activityId);

        // if we have a scene selector open, and the user clicks undo/redo, there's no guarantee that
        // the video will be in the same state as it was before the undo/redo action. To be safe, we should
        // close any scene selector that is open. Otherwise, if the user makes a selection in the scene selector,
        // we may try to add a scene somewhere that doesn't make any sense (could cause BSOD).
        dispatch(hideOverlaySelector());
        dispatch(hideFullscreenSelector());
        dispatch(hideFullscreenSwapSceneSelector());

        if (undoOrRedoAction) {
            dispatch(undoOrRedoAction());
        }

        luminary.sceneGroups.forEach((sceneGroup) => {
            dispatch(
                startLexicalControllingItsOwnState({
                    sceneGroupId: sceneGroup.id,
                }),
            );
        });

        const initialAutoUpdate = luminary.autoUpdate;
        luminary.autoUpdate = false;
        const videoData = getVideoData(getState());
        if (!videoData) throw new Error('Video data not found');
        luminary.clear().then(() => {
            ErrorReporter.addBreadcrumb({
                category: 'undo-redo',
                message: 'luminary cleared for undo-redo',
                level: 'info',
            });

            // Assets may be removed when undoing/redoing, so we clear the focussed asset to avoid using stale asset ids.
            dispatch(clearFocusedAsset());

            luminary.load(videoData);
            luminary.setTemplate(getTemplateBundle(getState(), luminary.template.id)!);
            luminary.autoUpdate = initialAutoUpdate;

            ErrorReporter.addBreadcrumb({
                category: 'undo-redo',
                message: 'luminary loaded after undo-redo',
                level: 'info',
            });

            luminary
                .onAllSlideImagesRendered()
                .then(() => {
                    const metricLabels = METRIC_UNDO_REDO.getLabels(luminary.scenes.length);
                    ActivityProfiler.end(activityId, METRIC_UNDO_REDO.name, metricLabels);
                })
                .catch();

            dispatch(hydrateTranslatedTranscriptsFromRedux());
            dispatch(hydrateLexicalEditorsFromRedux());
        });
    },
);

export const undoLastVideoActionOperation = createLuminaryOperation(({ call }) => {
    return call(performUndoRedoOperation, ActionCreators.undo);
});

export const redoLastVideoActionOperation = createLuminaryOperation(({ call }) => {
    return call(performUndoRedoOperation, ActionCreators.redo);
});

export const resetLuminaryAndLexicalToPresentCheckpointOperation = createLuminaryOperation(
    ({ call }) => {
        /*
    The undo-redo checkpoints should always be at "safe" spots: where luminary and lexical are in sync.
    We have some really complicated operations that involve coordination between luminary and lexical.
    Imagine a scenario where:

    - do some changes in luminary
    - do some changes in lexical -> hit a random error!

    In this case, lexical and luminary could become incompatible with each other. For example, maybe the
    transcript duration is longer than the voiceoverAsset duration.

    We can catch these errors and call this operation, which resets luminary and lexical to the CURRENT
    undo-redo checkpoint - whatever the safe state was right before we started making all the changes that errored.

    Note: this is expensive, so we should only call this when there's no hope of recovering from the error.
    */
        return call(performUndoRedoOperation, null);
    },
);

export const checkpointVideoOperation = createLuminaryOperation(
    (
        { luminary, dispatch }: LuminaryOperationContext<Luminary>,
        {
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: { systemTriggered?: boolean; parentActionGroup?: string } = {},
    ) => {
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
    },
);
